// src/components/AppLayout.js
// Updated: 11/10/2025 08:13 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '../context/AuthContext';
import { auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { AlertTriangle, LogOut } from 'lucide-react';

const logStopImpersonation = httpsCallable(functions, 'logStopImpersonation');

/**
 * Banner for admin impersonation
 */
function ImpersonationBanner() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check session storage to see if we are in an impersonation session
    const adminId = sessionStorage.getItem('impersonating_admin_id');
    if (adminId) {
      setIsImpersonating(true);
    }
  }, []);

  const handleStopImpersonating = async () => {
    try {
      // 1. Get the "revert ticket" and orgId from session storage
      const revertToken = sessionStorage.getItem('impersonation_revert_token');
      const adminOrgIdToLog = sessionStorage.getItem('impersonating_admin_org_id');

      // 2. Log out the current (impersonated) user
      await logout();

      // 3. Clear all session flags
      sessionStorage.removeItem('impersonating_admin_id');
      sessionStorage.removeItem('impersonation_revert_token');
      sessionStorage.removeItem('impersonating_admin_org_id');

      if (revertToken) {
        // 4. Use the revert token to sign the admin back in
        await signInWithCustomToken(auth, revertToken);
        
        // 5. Call the logging function AFTER successful re-authentication
        if (adminOrgIdToLog) {
          try {
            await logStopImpersonation({ originalOrgId: adminOrgIdToLog });
          } catch (logErr) {
            console.error("Failed to log impersonation stop:", logErr);
          }
        }
        
        // 6. Navigate to the dashboard (now as the admin)
        navigate('/dashboard');
      } else {
        // 7. Fallback if the token is missing for any reason
        navigate('/login');
      }
    } catch (err) {
      console.error('Failed to stop impersonating:', err);
      // Clear storage and navigate to login as a fallback
      sessionStorage.removeItem('impersonating_admin_id');
      sessionStorage.removeItem('impersonation_revert_token');
      sessionStorage.removeItem('impersonating_admin_org_id');
      navigate('/login');
      alert('Failed to stop impersonating. You have been logged out.');
    }
  };

  if (!isImpersonating) {
    return null; // Don't show anything if not impersonating
  }

  return (
    // --- FIX: Removed 'fixed inset-0' to restore correct banner sizing ---
    <div className="z-50 flex w-full items-center justify-center bg-yellow-500 p-3 text-black shadow-lg print-hide">
      <AlertTriangle className="mr-3 h-6 w-6" />
      <span className="mr-4 text-sm font-semibold">
        You are currently impersonating a user.
      </span>
      <button
        onClick={handleStopImpersonating}
        className="flex items-center rounded-md border border-black bg-white px-3 py-1 text-sm font-medium text-black transition hover:bg-gray-100"
      >
        <LogOut className="mr-2 h-4 w-4" />
        Stop Impersonating
      </button>
    </div>
  );
}


/**
 * This is the main layout for the app AFTER a user is logged in.
 * It combines the Sidebar, Header, and the main content area.
 * The `children` prop will be whatever page we are currently viewing
 * (e.g., DashboardPage, DocumentsPage).
 */
export default function AppLayout({ children }) {
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar (Navigation) */}
      <Sidebar />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* --- IMPERSONATION BANNER --- */}
        <ImpersonationBanner />
        
        {/* Header (User info, Logout) */}
        <Header />

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-6">
          <div className="mx-auto max-w-7xl">
            {/* The 'children' prop renders the active page here */}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}